/**
 * Load a hash of secrets provided by environment variables.
 * Local environments and fargate services use environment variables, Lambda does not.
 * This is a function so that is can be mutated during unit tests.
 */
function loadLocalSecrets() {
  return {
    /** Required for tests */
    TEST_DATA_KEY: process.env["TEST_DATA_KEY"],
    /** Required by app */
    ZENDESK_API_TOKEN: process.env["ZENDESK_API_TOKEN"],
    KLAVIYO_PRIVATE_KEY: process.env["KLAVIYO_PRIVATE_KEY"],
    /**
     * These tokens are used to sign server-side requests to the Storefront API.
     * @see https://shopify.dev/docs/api/usage/authentication#making-server-side-requests
     */
    SHOPIFY_STOREFRONT_PRIVATE_TOKEN_STAGING:
      process.env["SHOPIFY_STOREFRONT_PRIVATE_TOKEN_STAGING"],
    SHOPIFY_STOREFRONT_PRIVATE_TOKEN_PRODUCTION:
      process.env["SHOPIFY_STOREFRONT_PRIVATE_TOKEN_PRODUCTION"],
    /** Used for the admin API client (used in a few API endpoints). */
    SHOPIFY_ADMIN_TOKEN_STAGING: process.env["SHOPIFY_ADMIN_TOKEN_STAGING"],
    SHOPIFY_ADMIN_TOKEN_PRODUCTION:
      process.env["SHOPIFY_ADMIN_TOKEN_PRODUCTION"],
    /** Used for Customer Account API (authentication) */
    SHOPIFY_CUSTOMER_ACCOUNT_CLIENT_SECRET_PRODUCTION:
      process.env["SHOPIFY_CUSTOMER_ACCOUNT_CLIENT_SECRET_PRODUCTION"],
    SHOPIFY_CUSTOMER_ACCOUNT_CLIENT_SECRET_STAGING:
      process.env["SHOPIFY_CUSTOMER_ACCOUNT_CLIENT_SECRET_STAGING"],
    /** Used for Reviews.io */
    REVIEWS_IO_API_TOKEN: process.env["REVIEWS_IO_API_TOKEN"],
    FIREBASE_PRIVATE_KEY: process.env["FIREBASE_PRIVATE_KEY"],
    /** Used to sign JSON Web Tokens (JWT) */
    JWT_SECRET: process.env["JWT_SECRET"],
  } as const;
}

type Secrets = ReturnType<typeof loadLocalSecrets>;

/*
 * Fetch a value from Secrets Manager.
 * In Lambda environment, secrets are exposed via AWS lambda extension layer.
 * @see https://docs.aws.amazon.com/secretsmanager/latest/userguide/retrieving-secrets_lambda.html
 * @see https://docs.aws.amazon.com/systems-manager/latest/userguide/ps-integration-lambda-extensions.html
 */
async function fetchSecretFromSecretsManager(name: keyof Secrets) {
  const isLambda = !!process.env["AWS_LAMBDA_FUNCTION_NAME"];
  if (!isLambda) return null;

  const sessionToken = process.env["AWS_SESSION_TOKEN"];
  if (!sessionToken) return null;

  try {
    const response = await fetch(
      `http://localhost:2773/secretsmanager/get?secretId=${name}`,
      {
        headers: { "X-Aws-Parameters-Secrets-Token": sessionToken },
      },
    );
    const jsonResponse = (await response.json()) as { SecretString: string };
    return jsonResponse.SecretString;
  } catch {
    return null;
  }
}

/**
 * Fetch a secret with the given name.
 * First, the environment variables will be checked.
 * Then, if available, the Secrets Manager extension is called.
 */
export async function fetchSecret(name: keyof Secrets) {
  const localSecret = loadLocalSecrets()[name];

  if (!!localSecret) {
    // Replace the literal \n with an actual newline. New lines are tricky with environment variables.
    return localSecret.replace(/\\n/g, "\n");
  }

  return await fetchSecretFromSecretsManager(name);
}
